/**
 * Gift Tiers Manager - Полное управление подарками
 */

class GiftTiersManager {
  constructor(container) {
    this.container = container;
    this.giftPrice = parseInt(container.dataset.giftPrice) || 100000; // fallback
    this.isProcessing = false;
    this.processingVariants = new Set();
    this.lastAction = null;
    this.actionCooldown = 2000;
    this._lastGiftSignature = null;

    // Locale-backed notification strings (rendered into the container in
    // gift-tiers.liquid). {title} is replaced with the gift name at runtime.
    this.messages = {
      added: container.dataset.msgAdded || '{title} добавлен в корзину!',
      removed: container.dataset.msgRemoved || '{title} удалён из корзины',
      soldOut: container.dataset.msgSoldOut || '{title} распродан',
      already: container.dataset.msgAlready || 'Этот подарок уже в корзине',
      threshold: container.dataset.msgThreshold || 'Достигните необходимую сумму для этого подарка!',
      error: container.dataset.msgError || 'Произошла ошибка. Попробуйте ещё раз.',
    };

    this.init();
    this.observeCart();
    this.addNotificationStyles();
  }

  msg(key, title) {
    return (this.messages[key] || '').replace('{title}', title || '');
  }

  init() {
    const radioButtons = this.container.querySelectorAll('.gift-radio-button');
    radioButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleGiftAction(e));
    });

    // Валидация подарков при загрузке
    this.validateGiftsOnLoad();
  }

  /**
   * Проверка и удаление недействительных подарков
   */
  async validateGiftsOnLoad() {
    try {
      const cart = await this.getCart();
      const cartTotal = cart.total_price;
      const tiers = this.getTierThresholds();

      const invalidGifts = cart.items.filter(item => {
        if (!item.properties || !item.properties._gift_tier) return false;

        const giftTier = parseInt(item.properties._gift_tier);
        const tierThreshold = tiers.find(t => t.level === giftTier)?.threshold || 0;

        return cartTotal < tierThreshold;
      });

      if (invalidGifts.length > 0) {
        console.log('🗑️ Removing invalid gifts:', invalidGifts.length);
        const parsedState = await this.removeInvalidGifts(invalidGifts);
        // Refresh in place instead of reloading the whole page.
        await this.publishCartUpdate(parsedState);
        await this.refreshSection(true);
      }
    } catch (error) {
      console.error('Error validating gifts:', error);
    }
  }

  getTierThresholds() {
    const tierElements = this.container.querySelectorAll('[data-tier]');
    const tiers = [];

    tierElements.forEach(el => {
      tiers.push({
        level: parseInt(el.dataset.tier),
        threshold: parseInt(el.dataset.threshold)
      });
    });

    return tiers;
  }

  async getCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Failed to fetch cart');
    return await response.json();
  }

  /**
   * Collect the cart sections the theme has registered, so the Section
   * Rendering API returns fresh HTML for them (mirrors assets/cart.js).
   */
  getSectionsToBundle() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  /**
   * Re-render the cart (items, totals, drawer) without a full page reload:
   * fetch the fresh cart, attach the section HTML returned by the mutate call,
   * and publish the theme's cartUpdate event so every registered cart component
   * updates itself.
   */
  async publishCartUpdate(parsedState) {
    try {
      const cartJson = await this.getCart();
      if (parsedState && parsedState.sections) {
        cartJson.sections = parsedState.sections;
      }
      if (window.FoxTheme?.pubsub?.publish && window.FoxTheme.pubsub.PUB_SUB_EVENTS?.cartUpdate) {
        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartJson } }));
    } catch (error) {
      console.error('Error publishing cart update:', error);
    }
  }

  async removeInvalidGifts(gifts) {
    const updates = {};

    gifts.forEach(gift => {
      updates[gift.key] = 0;
    });

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        updates,
        sections: this.getSectionsToBundle(),
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) throw new Error('Failed to remove invalid gifts');
    return await response.json();
  }

  observeCart() {
    let refreshTimeout;
    const debouncedRefresh = () => {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        if (!this.isProcessing) {
          this.refreshSection();
        }
      }, 1000);
    };

    document.addEventListener('cart:updated', debouncedRefresh);

    // Канонический сигнал темы: публикуется при ЛЮБОМ изменении корзины,
    // включая удаление строки-подарка из списка товаров (когда сумма не меняется,
    // т.к. подарок бесплатный). Без этого карточка тира оставалась «выбрана».
    if (window.FoxTheme?.pubsub?.subscribe && window.FoxTheme.pubsub.PUB_SUB_EVENTS?.cartUpdate) {
      this._pubsubUnsub = window.FoxTheme.pubsub.subscribe(
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        () => debouncedRefresh()
      );
    }

    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      const observer = new MutationObserver(debouncedRefresh);
      observer.observe(cartForm, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value']
      });
    }

    document.querySelectorAll('input[name="updates[]"]').forEach(input => {
      input.addEventListener('change', () => {
        debouncedRefresh();
        // Также валидируем подарки при изменении количества
        setTimeout(() => this.validateGiftsOnLoad(), 1500);
      });
    });

    document.querySelectorAll('.quantity__button, [name="plus"], [name="minus"]').forEach(button => {
      button.addEventListener('click', () => {
        debouncedRefresh();
        setTimeout(() => this.validateGiftsOnLoad(), 1500);
      });
    });
  }

  // Сигнатура выбранных подарков в корзине (variant:tier). Меняется при
  // добавлении/удалении подарка даже если сумма корзины не изменилась.
  getGiftSignature(cart) {
    return cart.items
      .filter(i => i.properties && i.properties._gift_tier)
      .map(i => `${i.variant_id}:${i.properties._gift_tier}`)
      .sort()
      .join(',');
  }

  async refreshSection(force = false) {
    try {
      const cart = await this.getCart();
      const currentTotal = parseInt(this.container.dataset.cartTotal);
      const giftSignature = this.getGiftSignature(cart);

      if (force || cart.total_price !== currentTotal || giftSignature !== this._lastGiftSignature) {
        const sectionId = this.container.dataset.sectionId;
        const url = `${window.location.pathname}?section_id=${sectionId}`;

        const response = await fetch(url);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector('.gift-tiers-wrapper');

        if (newSection) {
          this.container.innerHTML = newSection.innerHTML;
          this.container.dataset.cartTotal = cart.total_price;
          this._lastGiftSignature = giftSignature;
          this.init();
        }
      }
    } catch (error) {
      console.error('Error refreshing section:', error);
    }
  }

  async handleGiftAction(e) {
    e.preventDefault();
    e.stopPropagation();

    if (this.isProcessing) {
      console.log('🔒 Already processing');
      return;
    }

    const button = e.currentTarget;

    if (button.disabled) {
      return;
    }

    const action = button.dataset.action;
    const variantId = button.dataset.variantId;
    const tier = button.dataset.tier;
    const productTitle = button.dataset.productTitle;

    const actionKey = `${action}-${variantId}-${tier}`;
    if (this.processingVariants.has(actionKey)) {
      console.log('🔒 Variant already processing');
      return;
    }

    const now = Date.now();
    if (this.lastAction && (now - this.lastAction.timestamp) < this.actionCooldown) {
      console.log('🔒 Cooldown active');
      return;
    }

    if (this.lastAction &&
      this.lastAction.key === actionKey &&
      (now - this.lastAction.timestamp) < 5000) {
      console.log('🔒 Duplicate action');
      return;
    }

    const giftItem = button.closest('.gift-item');
    const giftTitle = giftItem.querySelector('.gift-title')?.textContent || productTitle;

    // Дешёвая проверка доступности из data-атрибута (полную проверку наличия
    // делает сам /cart/add.js — его ошибка обрабатывается в catch ниже).
    const isAvailable = giftItem.dataset.available === 'true';
    if (!isAvailable && action === 'add') {
      console.log('❌ Product not available');
      this.showNotification(this.msg('soldOut', giftTitle), 'error');
      return;
    }

    this.isProcessing = true;
    this.processingVariants.add(actionKey);
    this.lastAction = { key: actionKey, timestamp: now };

    this.container.querySelectorAll('.gift-radio-button').forEach(btn => {
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
    });

    button.classList.add('loading');
    giftItem.style.opacity = '0.5';
    giftItem.style.pointerEvents = 'none';

    console.log(`🎁 ${action} variant ${variantId}, tier ${tier}`);

    try {
      let parsedState;

      if (action === 'add') {
        const cartCheck = await this.getCart();

        // Проверяем порог
        const tierThreshold = parseInt(button.closest('[data-threshold]').dataset.threshold);
        if (cartCheck.total_price < tierThreshold) {
          this.showNotification(this.msg('threshold'), 'error');
          this.processingVariants.delete(actionKey);
          this.isProcessing = false;
          this.restoreUI(giftItem, button);
          return;
        }

        // Проверяем дубликат
        const alreadyExists = cartCheck.items.some(item => {
          if (item.variant_id != variantId) return false;
          if (!item.properties || !item.properties._gift_tier) return false;
          return item.properties._gift_tier == tier;
        });

        if (alreadyExists) {
          console.log('❌ Already in cart');
          this.showNotification(this.msg('already'), 'error');
          this.processingVariants.delete(actionKey);
          this.isProcessing = false;
          await this.refreshSection(true);
          return;
        }

        parsedState = await this.addGiftToCart(variantId, tier, productTitle);
        this.showNotification(this.msg('added', giftTitle), 'success');

      } else {
        parsedState = await this.removeGiftFromCart(variantId, tier, productTitle);
        this.showNotification(this.msg('removed', giftTitle), 'info');
      }

      // Обновляем корзину (товары + итог) и UI подарков без перезагрузки страницы.
      await this.publishCartUpdate(parsedState);
      await this.refreshSection(true);

      this.processingVariants.clear();
      this.isProcessing = false;

    } catch (error) {
      console.error('❌ Error:', error);

      // Обработка специфичной ошибки "out of stock"
      let errorMessage = this.msg('error');
      const raw = (error && error.message) || '';
      if (raw.includes('распродан') || raw.toLowerCase().includes('out of stock') || raw.toLowerCase().includes('sold out')) {
        errorMessage = this.msg('soldOut', giftTitle);
      }

      this.showNotification(errorMessage, 'error');

      this.restoreUI(giftItem, button);
      this.processingVariants.delete(actionKey);
      this.isProcessing = false;
    }
  }

  async addGiftToCart(variantId, tier, productTitle) {
    const uniqueId = `gift-tier-${tier}-${Date.now()}`;

    const formData = {
      items: [{
        id: variantId,
        quantity: 1,
        price: this.giftPrice,
        properties: {
          '_gift_tier': tier,
          '_gift_item': 'true',
          '_gift_id': uniqueId
        }
      }],
      sections: this.getSectionsToBundle(),
      sections_url: window.location.pathname
    };

    console.log('Adding to cart:', formData);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Add to cart failed:', error);

      // Пробрасываем понятное сообщение об ошибке
      throw new Error(error.description || error.message || 'Failed to add');
    }

    return await response.json();
  }

  restoreUI(giftItem, button) {
    giftItem.style.opacity = '1';
    giftItem.style.pointerEvents = 'auto';
    button.classList.remove('loading');

    this.container.querySelectorAll('.gift-radio-button').forEach(btn => {
      btn.disabled = false;
      btn.style.pointerEvents = 'auto';
    });
  }

  async removeGiftFromCart(variantId, tier, productTitle) {
    const cart = await this.getCart();

    const item = cart.items.find(i => {
      if (i.variant_id != variantId) return false;
      if (!i.properties) return false;
      return i.properties._gift_tier == tier;
    });

    if (!item) {
      throw new Error('Gift not found');
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: item.key,
        quantity: 0,
        sections: this.getSectionsToBundle(),
        sections_url: window.location.pathname
      })
    });

    if (!response.ok) throw new Error('Failed to remove');
    return await response.json();
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `gift-notification gift-notification--${type}`;
    notification.textContent = message;

    const colors = {
      success: '#00704a',
      error: '#dc2626',
      info: '#0284c7'
    };

    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      background: ${colors[type] || colors.success};
      color: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      animation: slideIn 0.3s ease;
      max-width: 300px;
      font-size: 0.9rem;
      font-weight: 500;
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  addNotificationStyles() {
    if (document.getElementById('gift-notification-styles')) return;

    const style = document.createElement('style');
    style.id = 'gift-notification-styles';
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(400px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(400px); opacity: 0; }
      }
      .gift-radio-button.loading {
        opacity: 0.5;
        cursor: wait !important;
      }
    `;
    document.head.appendChild(style);
  }
}

// Экспорт в window для использования в секции
window.GiftTiersManager = GiftTiersManager;

// Авто-инициализация если находимся на странице корзины
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGiftTiers);
} else {
  initGiftTiers();
}

function initGiftTiers() {
  const container = document.querySelector('.gift-tiers-wrapper');
  if (container && !container.dataset.initialized) {
    // Получаем gift_price из section settings
    const giftPrice = container.closest('[data-section-id]')?.querySelector('[data-gift-price]')?.dataset.giftPrice;
    if (giftPrice) {
      container.dataset.giftPrice = giftPrice;
    }

    new GiftTiersManager(container);
    container.dataset.initialized = 'true';
  }
}
